import React, { useRef, useEffect, useState } from "react";
import { Box, IconButton, Tooltip, Select, MenuItem } from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";

interface TextAreaProps {
  label?: string;
  labelClassName?: string;
  value: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  required?: boolean;
  className?: string;
  style?: React.CSSProperties;
  id?: string;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  onFocus?: React.FocusEventHandler<HTMLDivElement>;
}

const TextArea: React.FC<TextAreaProps> = ({
  label,
  labelClassName = "",
  value,
  onChange,
  placeholder,
  readOnly = false,
  disabled = false,
  autoFocus = false,
  required = false,
  className = "",
  style,
  id,
  onBlur,
  onFocus,
}) => {
  const divRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [bold, setBold] = useState(false);
  const [italic, setItalic] = useState(false);
  const [underline, setUnderline] = useState(false);
  const [fontSize, setFontSize] = useState(14);

  const textAreaId =
    id || `textarea-${Math.random().toString(36).substr(2, 9)}`;

  //////////////////////////////////////////////////
  //// FONT SIZE
  //////////////////////////////////////////////////

  const applyFontSize = (size: number) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    const range = sel.getRangeAt(0);
    if (range.collapsed) return;

    const span = document.createElement("span");
    span.appendChild(range.extractContents());
    span.style.fontSize = size + "px";

    range.insertNode(span);
    range.setStartAfter(span);

    sel.removeAllRanges();
    sel.addRange(range);

    setFontSize(size);
    handleInput();
  };

  //////////////////////////////////////////////////
  //// CLEAN HTML
  //////////////////////////////////////////////////

  const cleanHtml = (html: string) => {
    const temp = document.createElement("div");
    temp.innerHTML = html;

    const walk = (node: Node) => {
      if (node.nodeType !== 1) return;

      const el = node as HTMLElement;
      const children = Array.from(el.childNodes);

      el.removeAttribute("class");

      if (el.tagName !== "A" && el.tagName !== "IMG") {
        el.removeAttribute("style");
      }

      const allowed = ["B", "STRONG", "I", "EM", "U", "A", "IMG"];

      if (!allowed.includes(el.tagName)) {
        const fragment = document.createDocumentFragment();
        children.forEach((child) => fragment.appendChild(child));
        el.replaceWith(fragment);
        return;
      }

      children.forEach(walk);

      if (el.tagName === "A") {
        const a = el as HTMLAnchorElement;

        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noopener noreferrer");

        a.style.cursor = "pointer";
        a.style.color = "#1976d2";
        a.style.textDecoration = "underline";
      }
    };

    Array.from(temp.childNodes).forEach(walk);

    return temp.innerHTML;
  };

  //////////////////////////////////////////////////
  //// TOOLBAR STATE
  //////////////////////////////////////////////////

  const updateToolbar = () => {
    setBold(document.queryCommandState("bold"));
    setItalic(document.queryCommandState("italic"));
    setUnderline(document.queryCommandState("underline"));
  };

  const handleInput = () => {
    if (divRef.current && !readOnly && !disabled) {
      onChange?.(divRef.current.innerHTML);
    }
    updateToolbar();
  };

  //////////////////////////////////////////////////
  //// FORMATTING
  //////////////////////////////////////////////////

  const applyFormat = (cmd: "bold" | "italic" | "underline") => {
    if (!divRef.current) return;

    divRef.current.focus();
    document.execCommand(cmd, false);
    handleInput();
  };

  //////////////////////////////////////////////////
  //// NORMALIZE
  //////////////////////////////////////////////////

  const normalizeSelection = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    const range = sel.getRangeAt(0);
    const fragment = range.cloneContents();

    const container = document.createElement("div");
    container.appendChild(fragment);

    const clean = cleanHtml(container.innerHTML);

    const span = document.createElement("span");
    span.innerHTML = clean;
    span.style.fontFamily = "Arial";
    span.style.fontSize = "14px";

    range.deleteContents();
    range.insertNode(span);

    range.setStartAfter(span);
    sel.removeAllRanges();
    sel.addRange(range);

    handleInput();
  };

  const normalizeAll = () => {
    if (!divRef.current) return;

    const clean = cleanHtml(divRef.current.innerHTML);
    divRef.current.innerHTML = clean;

    handleInput();
  };

  //////////////////////////////////////////////////
  //// INSERT IMAGE
  //////////////////////////////////////////////////

  const insertHtmlAtCursor = (html: string) => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    const range = sel.getRangeAt(0);
    range.deleteContents();

    const el = document.createElement("div");
    el.innerHTML = html;

    const frag = document.createDocumentFragment();
    let node, lastNode;

    while ((node = el.firstChild)) {
      lastNode = frag.appendChild(node);
    }

    range.insertNode(frag);

    if (lastNode) {
      range.setStartAfter(lastNode);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }

    handleInput();
  };

  const handleFileInsert = (file: File) => {
    const url = URL.createObjectURL(file);

    if (file.type.startsWith("image/")) {
      const reader = new FileReader();

      reader.onload = (e) => {
        insertHtmlAtCursor(
          `<img src="${e.target?.result}" style="max-width:100%"/>`,
        );
      };

      reader.readAsDataURL(file);
      return;
    }

    insertHtmlAtCursor(
      `<a href="${url}" download="${file.name}" style="color:#1976d2; text-decoration:underline; cursor:pointer;">
        📧 ${file.name}
      </a>`,
    );
  };

  const focusEditor = () => {
    if (!divRef.current) return;

    divRef.current.focus();

    const sel = window.getSelection();
    if (!sel) return;

    const range = document.createRange();
    range.selectNodeContents(divRef.current);
    range.collapse(false);

    sel.removeAllRanges();
    sel.addRange(range);
  };

  //////////////////////////////////////////////////
  //// FIX: DOWNLOAD CLICK HANDLING (IMPORTANT)
  //////////////////////////////////////////////////

  const handleClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;

    if (target.tagName === "A") {
      const a = target as HTMLAnchorElement;

      // FORCE download for blob links
      if (a.href.startsWith("blob:")) {
        e.preventDefault();

        const link = document.createElement("a");
        link.href = a.href;
        link.download = a.download || "file.msg";
        link.click();
      }
    }
  };

  //////////////////////////////////////////////////
  //// DROP
  //////////////////////////////////////////////////

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (readOnly || disabled) return;

    focusEditor();

    const dt = e.dataTransfer;
    let handled = false;

    if (dt.files && dt.files.length > 0) {
      const files = Array.from(dt.files);

      files.forEach((file) => {
        if (file.size > 0) {
          handled = true;
          handleFileInsert(file);
        }
      });
    }

    if (!handled) {
      const text = dt.getData("text/plain");
      if (text) insertHtmlAtCursor(text);
    }

    if (!handled) {
      const html = dt.getData("text/html");
      if (html) insertHtmlAtCursor(html);
    }
  };

  //////////////////////////////////////////////////
  //// EFFECTS
  //////////////////////////////////////////////////

  useEffect(() => {
    if (divRef.current && divRef.current.innerHTML !== value) {
      divRef.current.innerHTML = value || "";
    }
  }, [value]);

  useEffect(() => {
    const handler = () => updateToolbar();
    document.addEventListener("selectionchange", handler);
    return () => document.removeEventListener("selectionchange", handler);
  }, []);

  //////////////////////////////////////////////////
  //// UI (UNCHANGED)
  //////////////////////////////////////////////////

  return (
    <Box className="input-container">
      {label && (
        <label htmlFor={textAreaId} className={`input-label ${labelClassName}`}>
          {label}
          {required && <span className="required">*</span>}
        </label>
      )}

      <Box
        sx={{
          position: "relative",
          width: "100%",
          border: isDragging ? "2px dashed #1976d2" : "1px solid #ccc",
          borderRadius: 1,
          padding: "6px",
          backgroundColor: disabled ? "#f5f5f5" : "inherit",
        }}
      >
        {/* Toolbar */}
        {!readOnly && !disabled && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              borderBottom: "1px solid #ddd",
              paddingBottom: "6px",
              marginBottom: "6px",
            }}
          >
            <Tooltip title="Bold">
              <IconButton
                size="small"
                onClick={() => applyFormat("bold")}
                color={bold ? "primary" : "default"}
              >
                <b>B</b>
              </IconButton>
            </Tooltip>

            <Tooltip title="Italic">
              <IconButton
                size="small"
                onClick={() => applyFormat("italic")}
                color={italic ? "primary" : "default"}
              >
                <i>I</i>
              </IconButton>
            </Tooltip>

            <Tooltip title="Underline">
              <IconButton
                size="small"
                onClick={() => applyFormat("underline")}
                color={underline ? "primary" : "default"}
              >
                <u>U</u>
              </IconButton>
            </Tooltip>

            <Select
              size="small"
              value={fontSize}
              onChange={(e) => applyFontSize(Number(e.target.value))}
              sx={{ width: 70, height: 30 }}
            >
              {[
                10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30, 32, 34, 36, 38, 40,
              ].map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>

            <Tooltip title="Normalize selection">
              <IconButton size="small" onClick={normalizeSelection}>
                📝
              </IconButton>
            </Tooltip>

            <Tooltip title="Clean all">
              <IconButton size="small" onClick={normalizeAll}>
                🧹
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Editor */}
        <Box
          id={textAreaId}
          ref={divRef}
          contentEditable={!readOnly && !disabled}
          className={`input-field ${className}`}
          sx={{
            minHeight: 100,
            outline: "none",
            whiteSpace: "pre-wrap",
            overflowY: "auto",
            padding: "4px",
            ...style,
          }}
          onInput={handleInput}
          onDrop={handleDrop}
          onClickCapture={handleClickCapture}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={(e) => setIsDragging(false)}
          onBlur={onBlur}
          onFocus={onFocus}
          suppressContentEditableWarning
          spellCheck
        />

        {/* Image button */}
        {!readOnly && !disabled && (
          <>
            <input
              type="file"
              accept="image/*"
              hidden
              ref={imageInputRef}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFileInsert(f);
              }}
            />

            <Tooltip title="Insert image">
              <IconButton
                size="small"
                sx={{ position: "absolute", top: 6, right: 6 }}
                onClick={() => imageInputRef.current?.click()}
              >
                <ImageIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
};

export default TextArea;
